import React, { useEffect, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { FiTrash2, FiMinus, FiPlus } from "react-icons/fi";

const IconButton = ({ onClick, children, label, className, style }) => (
	<button
		type="button"
		onClick={onClick}
		aria-label={label}
		className={`flex items-center justify-center ${className || ""}`}
		style={style}
	>
		{children}
	</button>
);

const QuantityController = ({ id, quantity, stock, onIncreaseOptimistic }) => {
	const [localQty, setLocalQty] = React.useState(quantity);
	const [showLimitMsg, setShowLimitMsg] = React.useState(false);
	React.useEffect(() => { setLocalQty(quantity); }, [quantity]);
	React.useEffect(() => {
		if (showLimitMsg) {
			setTimeout(() => setShowLimitMsg(false), 2000);
		}
	}, [showLimitMsg]);
	const handleIncrease = async () => {
		const currentQty = localQty || 1;
		if (currentQty >= stock) {
			setShowLimitMsg(true);
			return;
		}
		try {
			const token = localStorage.getItem("userToken");
			const headers = { "Content-Type": "application/json" };
			if (token) headers["Authorization"] = `Bearer ${token}`;
			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart/add`, {
				method: "POST",
				headers,
				credentials: "include",
				body: JSON.stringify({ productId: id, quantity: 1 }) // Always add 1
			});
			if (!response.ok) throw new Error("Failed to update cart");
			// Fetch latest cart from backend
			const cartToken = localStorage.getItem("userToken");
			const cartHeaders = { "Content-Type": "application/json" };
			if (cartToken) cartHeaders["Authorization"] = `Bearer ${cartToken}`;
			const cartRes = await fetch(`${process.env.REACT_APP_API_URL}/api/cart`, { credentials: "include", headers: cartHeaders });
			if (!cartRes.ok) throw new Error("Failed to fetch cart");
			const cartData = await cartRes.json();
			const updatedItem = (cartData.data.items || []).find(item => item.productId === id);
			const newQty = updatedItem ? updatedItem.quantity : currentQty + 1;
			setLocalQty(newQty);
			if (onIncreaseOptimistic) onIncreaseOptimistic(newQty);
		} catch (err) {
			// Do not update UI
		}
	};
	return (
		<>
			{showLimitMsg && (
				<div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-[#19C463] text-white px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut"
					style={{ minWidth: '220px', textAlign: 'center', letterSpacing: '0.5px' }}>
					No more product to add to cart ðŸ˜Š
				</div>
			)}
			<style>
				{`
					@keyframes fadeInOut {
						0% { opacity: 0; }
						10% { opacity: 1; }
						90% { opacity: 1; }
						100% { opacity: 0; }
					}
					.animate-fadeInOut {
						animation: fadeInOut 2s;
					}
				`}
			</style>
			<div className="flex items-center gap-2" style={{ position: 'relative' }}>
				<IconButton
					/* No onClick for minus button */
					label="Decrease quantity"
					className="h-8 w-8"
					style={{
						borderRadius: 4,
						boxShadow: '0px 2px 8px rgba(0,0,0,0.25)',
						backgroundColor: '#19C463',
						color: '#fff',
						border: 'none',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<FiMinus className="h-4 w-4" />
				</IconButton>
				<span className="min-w-[24px] text-center text-sm font-semibold text-gray-900">
					{localQty}
				</span>
				<IconButton
					onClick={handleIncrease}
					label="Increase quantity"
					className="h-8 w-8"
					style={{
						borderRadius: 4,
						boxShadow: '0px 2px 8px rgba(0,0,0,0.25)',
						backgroundColor: '#19C463',
						color: '#fff',
						border: 'none',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<FiPlus className="h-4 w-4" />
				</IconButton>
			</div>
		</>
	);
};

const CartItemCard = ({ item, onRemove, onIncreaseOptimistic }) => (
				<div className="rounded-2xl bg-white p-4 shadow-sm" style={{ borderRadius: 0, paddingTop: 8, paddingLeft: 8, paddingRight: 8, paddingBottom: 8, gap: 8, display: 'flex', flexDirection: 'column', marginTop: 0, marginBottom: 0 }}>
		<div className="flex gap-4">
			<div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-gray-50">
				<img
					loading="lazy"
					src={item.image}
					alt={item.description}
					className="h-full w-full object-cover"
				/>
			</div>
			<div className="flex-1" style={{ textAlign: 'left' }}>
				<p className="text-sm font-semibold text-gray-900 line-clamp-2">
					{item.description}
				</p>
				<p className="mt-1 text-sm font-semibold text-gray-900">
					{item.price}
				</p>
				<p className="mt-1 text-xs text-gray-600">
					Sellers: {item.seller}
				</p>
				{item.stock <= 5 && (
					<p className="mt-1 text-xs font-semibold text-[#19C463]">
						{item.stock} Left
					</p>
				)}
			</div>
		</div>
		<div className="mt-4 flex items-center justify-between" style={{ marginTop: 0 }}>
			<IconButton
				onClick={() => onRemove(item.itemId)}
				label="Remove item"
				className="flex items-center gap-2 text-sm text-gray-600"
				style={{ color: '#19C463' }}
			>
				<FiTrash2 className="h-4 w-4" style={{ color: '#19C463' }} />
				<span style={{ color: '#19C463' }}>Remove</span>
			</IconButton>
			<QuantityController
					id={item.id}
					quantity={item.quantity}
					stock={item.stock}
					onIncreaseOptimistic={newQty => onIncreaseOptimistic(item, newQty)}
			/> 
		</div>
	</div>
);

const CartScreen = () => {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [subtotal, setSubtotal] = useState(0);
	const navigate = require("react-router-dom").useNavigate();

	const calculateSubtotal = (cartItems) =>
		cartItems.reduce((sum, item) => {
			const price = typeof item.priceValue === "number" ? item.priceValue : 0;
			return sum + price * item.quantity;
		}, 0);

	useEffect(() => {
		const fetchCart = async () => {
			setLoading(true);
			try {
				const token = localStorage.getItem("userToken");
				const headers = { "Content-Type": "application/json" };
				if (token) headers["Authorization"] = `Bearer ${token}`;
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart`, { credentials: "include", headers });
				if (!response.ok) throw new Error("Failed to fetch cart");
				const data = await response.json();
				// Map backend data to UI items using correct backend fields
				const mapped = (data.data.items || []).map((item) => ({
					id: item.productId,
					itemId: item._id || item.itemId,
					description: item.description, // main description
					price: `₦${Number(item.price || 0).toLocaleString("en-NG")}`, // price as currency
					priceValue: Number(item.price || 0),
					seller: item.vendorName, // seller name
					stock: item.stock, // stock left
					image: (item.productImages && item.productImages[0]) || item.productImage || '',
					quantity: item.quantity,
				}));
				setItems(mapped);
				setSubtotal(calculateSubtotal(mapped));
			} catch (err) {
				setItems([]);
				setSubtotal(0);
			} finally {
				setLoading(false);
			}
		};
		fetchCart();
	}, []);

	const handleRemoveItem = async (itemId) => {
		if (!itemId) return;
		try {
			const token = localStorage.getItem("userToken");
			const headers = { "Content-Type": "application/json" };
			if (token) headers["Authorization"] = `Bearer ${token}`;
			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart/item/${itemId}`, {
				method: "DELETE",
				headers,
				credentials: "include",
			});
			const data = await response.json();
			if (!response.ok || !data.success) {
				throw new Error(data.message || "Error removing item.");
			}
			setItems((prevItems) => {
				const updatedItems = prevItems.filter((item) => item.itemId !== itemId);
				setSubtotal(calculateSubtotal(updatedItems));
				return updatedItems;
			});
		} catch (err) {
			// Keep current cart view if remove fails.
		}
	};





	return (
		<div className="min-h-screen bg-[#F1F3F2] text-gray-900">
			<header className="fixed top-0 left-0 right-0 z-30 bg-white shadow-sm">
				<div className="flex items-center justify-between p-2">
					<IconButton
						onClick={() => window.history.back()}
						label="Go back"
						className="flex items-center justify-center h-10 w-10 rounded-full bg-white text-gray-700"
						style={{ minWidth: '24px', minHeight: '24px' }}
					>
						<IoArrowBack className="h-5 w-5" style={{ color: '#000000' }} />
					</IconButton>
					<h1 className="text-base font-semibold text-gray-900" style={{ fontSize: '20px' }}>
						Cart <span className="text-gray-600" style={{ color: '#000000' }}>({items.length})</span>
					</h1>
					<div className="h-10 w-10" />
				</div>
			</header>

			<main className="px-0 pt-20 pb-28">
				<div className="cart-summary rounded-2xl bg-white p-4 shadow-sm" style={{ borderRadius: 0, paddingTop: 8, paddingLeft: 0, paddingRight: 0, paddingBottom: 0, backgroundColor: 'transparent', gap: 8, display: 'flex', flexDirection: 'column' }}>
					<h2 
						className="text-sm font-semibold text-gray-900" 
						style={{ 
							backgroundColor: 'transparent', 
							fontSize: 14, 
							textAlign: 'left', 
							paddingLeft: 8, 
							paddingTop: 4, 
							paddingBottom: 4, 
							paddingRight: 4 
						}}
					>
						Cart Summary
					</h2>
					<div style={{ backgroundColor: 'white', textAlign: 'left', gap: '10px', display: 'flex', flexDirection: 'column', paddingLeft: 8, paddingRight: 8, paddingTop: 0, paddingBottom: 0 }} className="text-left">
						<div className="mt-3 flex items-center justify-between text-sm text-gray-700" style={{ marginTop: 0 }}>
							<span style={{ padding: 4, fontSize: 14, fontWeight: 600, color: '#000000' }}>Subtotal</span>
							<span className="font-semibold text-gray-900" style={{ padding: 4, fontSize: 16, fontWeight: 600, color: '#000000' }}>₦{subtotal.toLocaleString("en-NG")}</span>
						</div>
						<div className="mt-2 text-xs text-gray-500" style={{ paddingTop: 4, paddingBottom: 4, margin: 0 }}>
							<p style={{ fontSize: 12, color: '#000000', margin: 0 }}>Delivery fee not included yet.</p>
						</div>
					</div>
				</div>

				<div className="mt-4 space-y-4" style={{ marginTop: 12, gap: 10, display: 'flex', flexDirection: 'column' }}>
					<p className="space-y-4 text-left text-[12px] font-bold pl-2 pt-1 pb-1 pr-1" style={{ color: '#000000' }}>
						CART (<span>{items.length}</span>)
					</p>
					{loading ? (
						<div className="text-center text-gray-500 py-8">Loading...</div>
					) : items.length === 0 ? (
						<div className="text-center text-gray-500 py-8">Your cart is empty.</div>
					) : (
						items.map((item) => (
							<CartItemCard
								key={item.id}
								item={item}
								onRemove={handleRemoveItem}
								onIncreaseOptimistic={(changedItem, newQty) => {
									setItems(prevItems => prevItems.map(it => it.id === changedItem.id ? { ...it, quantity: newQty } : it));
									setSubtotal(prevSubtotal => {
										const price = typeof changedItem.priceValue === 'number' ? changedItem.priceValue : 0;
										return prevSubtotal + price;
									});
								}}
							/>
						))
					)}
				</div>
			</main>

			<div className="fixed bottom-0 left-0 right-0 bg-white px-4 py-4 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]">
				<button
					type="button"
					className="w-full rounded-full bg-[#19C463] py-3 text-sm font-semibold text-white"
					style={{ borderRadius: 4 }}
					onClick={() => navigate('/checkout')}
				>
					Checkout ({items.length})
				</button>
			</div>
		</div>
	);
};

export default CartScreen;

